import {
  parseISO,
  isBefore,
  startOfDay,
  format,
  isValid,
  differenceInCalendarDays,
} from "date-fns";
import type { Locale } from "date-fns";

/**
 * Central date utility functions for consistent date handling.
 *
 * Exports:
 * - Overdue calculations: isDateOverdue, isInspectionOverdue, isDefectOverdue
 * - Safe formatting: formatSafeDate, formatDateRange, DATE_FORMATS
 * - Reference dates: getTodayStart, getTodayStartISO
 */

/**
 * Standard date format patterns used across the application.
 * Use these constants instead of hardcoding format strings.
 */
export const DATE_FORMATS = {
  /** dd.MM.yyyy - für Datumsanzeige */
  DATE: "dd.MM.yyyy",
  /** dd.MM.yy - für kompakte Anzeige */
  DATE_SHORT: "dd.MM.yy",
  /** dd.MM.yyyy HH:mm - für Datum mit Uhrzeit */
  DATETIME: "dd.MM.yyyy HH:mm",
  /** dd.MM.yyyy, HH:mm - Datum mit Komma-Trennung */
  DATETIME_COMMA: "dd.MM.yyyy, HH:mm",
  /** HH:mm:ss - nur Uhrzeit */
  TIME: "HH:mm:ss",
  /** HH:mm - kurze Uhrzeit */
  TIME_SHORT: "HH:mm",
  /** d. MMMM yyyy - ausgeschriebener Monat */
  DATE_LONG: "d. MMMM yyyy",
  /** d. MMMM yyyy, HH:mm - ausgeschrieben mit Uhrzeit */
  DATETIME_LONG: "d. MMMM yyyy, HH:mm",
  /** yyyy-MM-dd - für Gruppierung/Sortierung */
  ISO_DATE: "yyyy-MM-dd",
} as const;

export type DateFormatKey = keyof typeof DATE_FORMATS;

/**
 * Safely formats a date value, returning a fallback string if the date is invalid.
 * Prevents RangeError: Invalid time value.
 *
 * @param date - ISO string, Date object, or null/undefined
 * @param formatPattern - date-fns format pattern (use DATE_FORMATS constants)
 * @param options - Optional locale and fallback
 * @returns Formatted date string or fallback
 *
 * @example
 * formatSafeDate('2024-01-15', DATE_FORMATS.DATE) // '15.01.2024'
 * formatSafeDate(null, DATE_FORMATS.DATE) // '-'
 * formatSafeDate('invalid', DATE_FORMATS.DATE, { fallback: 'N/A' }) // 'N/A'
 */
export function formatSafeDate(
  date: string | Date | null | undefined,
  formatPattern: string = DATE_FORMATS.DATE,
  options?: {
    locale?: Locale;
    fallback?: string;
  },
): string {
  const { locale, fallback = "-" } = options || {};

  if (!date) return fallback;

  try {
    const parsedDate = typeof date === "string" ? parseISO(date) : date;

    if (!isValid(parsedDate)) return fallback;

    return format(parsedDate, formatPattern, locale ? { locale } : undefined);
  } catch {
    return fallback;
  }
}

/**
 * Safely format a date range for display.
 *
 * @param from - Start date
 * @param to - End date (optional)
 * @param locale - date-fns locale
 * @returns Formatted range string like "01.01.24 - 31.01.24" or single date
 *
 * @example
 * formatDateRange(new Date('2024-01-01'), new Date('2024-01-31')) // '01.01.24 - 31.01.24'
 * formatDateRange(new Date('2024-01-01')) // '01.01.2024'
 */
export function formatDateRange(
  from: Date | null | undefined,
  to?: Date | null,
  locale?: Locale,
): string {
  if (!from) return "";

  const fromStr = formatSafeDate(from, DATE_FORMATS.DATE_SHORT, { locale });

  if (to) {
    const toStr = formatSafeDate(to, DATE_FORMATS.DATE_SHORT, { locale });
    return `${fromStr} - ${toStr}`;
  }

  return formatSafeDate(from, DATE_FORMATS.DATE, { locale });
}

/**
 * Get the start of today (00:00:00) for consistent overdue calculations.
 * This is the single source of truth for the reference date.
 */
export function getTodayStart(): Date {
  return startOfDay(new Date());
}

/**
 * Get the start of today as ISO string for database queries.
 */
export function getTodayStartISO(): string {
  return getTodayStart().toISOString();
}

/**
 * Check if a due date is overdue (before the start of today).
 *
 * @param dueDate - ISO date string or Date object or null
 * @returns true if the due date is before today's start
 */
export function isDateOverdue(dueDate: string | Date | null | undefined): boolean {
  if (!dueDate) return false;

  const parsedDate = typeof dueDate === "string" ? parseISO(dueDate) : dueDate;
  return isBefore(parsedDate, getTodayStart());
}

// Type definitions for entity status
type InspectionOpenStatus = "pending" | "in_progress";
type DefectOpenStatus = "open" | "in_progress" | "resolved";

/**
 * Check if an inspection is overdue based on due_date and status.
 * Only open inspections (pending, in_progress) can be overdue.
 */
export function isInspectionOverdue(dueDate: string | null | undefined, status: string): boolean {
  const openStatuses: InspectionOpenStatus[] = ["pending", "in_progress"];
  if (!openStatuses.includes(status as InspectionOpenStatus)) return false;
  return isDateOverdue(dueDate);
}

/**
 * Check if a defect is overdue based on due_date and status.
 * Open defects (open, in_progress, resolved) can be overdue.
 * Resolved defects awaiting verification ARE considered overdue if past due date.
 */
export function isDefectOverdue(dueDate: string | null | undefined, status: string): boolean {
  const openStatuses: DefectOpenStatus[] = ["open", "in_progress", "resolved"];
  if (!openStatuses.includes(status as DefectOpenStatus)) return false;
  return isDateOverdue(dueDate);
}

/**
 * TES-2237 (US6, ehrliches Verlust-Framing): calendar days remaining until a due date, relative
 * to the start of today. Returns `null` for a missing/invalid date, `0` for "due today", and a
 * NEGATIVE number once the date is in the past (callers gate the anticipatory framing on
 * `isOverdue` separately — this stays a pure, honest day-count, never a manipulated value).
 */
export function daysUntilDue(dueDate: string | Date | null | undefined): number | null {
  if (!dueDate) return null;

  const parsedDate = typeof dueDate === "string" ? parseISO(dueDate) : dueDate;
  if (!isValid(parsedDate)) return null;

  return differenceInCalendarDays(startOfDay(parsedDate), getTodayStart());
}
